import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Plus, Search, Pencil, Trash2, User, KeyRound, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Pagination from '../components/Pagination';
import CommonLoader from '../components/CommonLoader';
import AddUser from '../components/modals/AddUser';
import { getStaffList, getUserRoles, setStaffStatus, deleteStaff, resetStaffPassword } from '../redux/staffSlice';
import { getFileURL } from '../utils/fileURL';

const MySwal = withReactContent(Swal);

// The tenant's own employees — reception, nursing, pharmacy, lab, scan, billing.
//
// Deliberately separate from Doctors and Patients. A doctor has a licence, a
// subscription and clinical authority; a patient is a global identity shared
// across hospitals. Neither is an employment record, and merging them into one
// "people" screen would mean one form trying to be three things.
export default function Users() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { staffList, staffTotal, staffListLoading, roles, mutating } = useSelector((s) => s.staff);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [activeTab, setActiveTab] = useState('active');
    const [roleFilter, setRoleFilter] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const searchTimeoutRef = useRef(null);

    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearch(searchQuery.trim());
            setCurrentPage(1);
        }, 500);
        return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
    }, [searchQuery]);

    // The role list drives both the filter and the create form's dropdown, so it
    // is fetched here once rather than by each of them.
    useEffect(() => { dispatch(getUserRoles({ limit: 100 })); }, [dispatch, refreshKey]);

    useEffect(() => {
        dispatch(getStaffList({
            page: currentPage,
            limit: itemsPerPage,
            search: debouncedSearch,
            status: activeTab,
            userRoleId: roleFilter ? [roleFilter] : [],
        }));
    }, [dispatch, currentPage, itemsPerPage, debouncedSearch, activeTab, roleFilter, refreshKey]);

    const refetch = () => setRefreshKey((k) => k + 1);

    const handleStatusToggle = async (staff) => {
        const isActive = staff.status === 'active';
        const next = isActive ? 'inactive' : 'active';

        const result = await MySwal.fire({
            title: isActive
                ? t('userpage.deactivate_title', 'Deactivate this user?')
                : t('userpage.activate_title', 'Activate this user?'),
            // Deactivation is immediate and total — worth saying plainly, because
            // an admin doing it mid-shift needs to know the person will be cut
            // off from the screen they are looking at right now.
            text: isActive
                ? t('userpage.deactivate_text', 'They will be signed out of anything they have open and cannot sign in again until reactivated.')
                : t('userpage.activate_text', 'They will be able to sign in again straight away.'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('userpage.confirm', 'Confirm'),
            cancelButtonText: t('userpage.cancel', 'Cancel'),
        });
        if (!result.isConfirmed) return;

        try {
            await dispatch(setStaffStatus({ staffId: staff.staffId, status: next })).unwrap();
            toast.success(next === 'active'
                ? t('userpage.activated', 'User activated')
                : t('userpage.deactivated', 'User deactivated'));
            refetch();
        } catch (err) {
            toast.warn(typeof err?.error === 'string' ? err.error : t('userpage.status_error', 'Failed to update the status'));
        }
    };

    const handleDelete = async (staff) => {
        const result = await MySwal.fire({
            title: t('userpage.delete_title', 'Delete this user?'),
            text: t('userpage.delete_text', 'Their login is removed. What they did stays in the audit log.'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('userpage.delete', 'Delete'),
            cancelButtonText: t('userpage.cancel', 'Cancel'),
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                try {
                    return await dispatch(deleteStaff(staff.staffId)).unwrap();
                } catch (err) {
                    toast.warn(typeof err?.error === 'string' ? err.error : t('userpage.delete_error', 'Failed to delete the user'));
                    throw err;
                }
            },
            allowOutsideClick: () => !MySwal.isLoading(),
        });
        if (result.isConfirmed) {
            toast.success(t('userpage.deleted', 'User deleted'));
            refetch();
        }
    };

    // The daily "I'm locked out" at the front desk. A RESET NEVER PUTS A
    // PASSWORD IN A MAIL OR ON A SCREEN (owner, 2 Oct 2026): the server makes
    // the current password stop working and tells the employee, by e-mail or
    // SMS, to set a new one with Forgot password. Nothing is shown to read out.
    const handleResetPassword = async (staff) => {
        const result = await MySwal.fire({
            title: t('passwordReset.confirm_title'),
            html: `<p style="font-size:14px;color:#475569">${t('passwordReset.confirm_text')}</p>
                   <p style="margin-top:10px;font-size:12px;color:#64748b">${t('passwordReset.sessions_note')}</p>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('passwordReset.confirm'),
            cancelButtonText: t('passwordReset.cancel'),
        });
        if (!result.isConfirmed) return;

        try {
            const res = await dispatch(resetStaffPassword({ staffId: staff.staffId })).unwrap();
            await MySwal.fire({
                title: t('passwordReset.done_title'),
                text: res?.data?.noticeDelivered === false
                    ? t('passwordReset.done_not_sent')
                    : t('passwordReset.done_sent'),
                icon: 'success',
                confirmButtonColor: '#4ABDBD',
                confirmButtonText: t('passwordReset.done'),
            });
        } catch (err) {
            toast.warn(typeof err?.error === 'string' ? err.error : t('passwordReset.error'));
        }
    };

    const openCreate = () => { setEditing(null); setIsModalOpen(true); };
    const openEdit = (staff) => { setEditing(staff); setIsModalOpen(true); };

    const handlePageChange = (page, newItemsPerPage) => {
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
    };

    const indexOfFirst = (currentPage - 1) * itemsPerPage;

    const initialsOf = (s) =>
        `${s.name ? s.name.charAt(0).toUpperCase() : ''}${s.surname ? s.surname.charAt(0).toUpperCase() : ''}`;

    return (
        <div data-testid="users-page-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <div className="ml-2">
                    <h1 data-testid="users-page-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                        {t('userpage.title', 'Users')}
                    </h1>
                    <p className="text-xs text-slate-400 mt-1">
                        {t('userpage.subtitle', 'The people who work here — reception, nursing, pharmacy, lab, scan and billing.')}
                    </p>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                    <button
                        data-testid="manage-roles-button"
                        onClick={() => navigate('/user-roles')}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl border border-[#4ABDBD] text-[#2D8A8A] font-semibold text-sm hover:bg-[#4ABDBD]/10 cursor-pointer whitespace-nowrap"
                    >
                        <ShieldCheck className="w-4 h-4" />
                        {t('userpage.manage_roles', 'Roles')}
                    </button>
                    <button
                        data-testid="add-user-button"
                        onClick={openCreate}
                        disabled={staffListLoading}
                        className={`group flex items-center gap-2 w-full sm:w-auto cursor-pointer px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm whitespace-nowrap ${staffListLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <Plus className="w-5 h-5" />
                        {t('userpage.add_user', 'Add user')}
                    </button>
                </div>
            </div>

            <div className="mb-6 flex flex-col sm:flex-row gap-2 sm:items-center">
                <div className="relative flex bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 gap-2">
                    {['active', 'inactive'].map((tab) => (
                        <button
                            key={tab}
                            data-testid={`users-tab-${tab}`}
                            onClick={() => { setActiveTab(tab); setCurrentPage(1); }}
                            className={`relative px-6 py-2 text-sm rounded-xl transition-colors duration-300 flex-1 z-10 ${activeTab === tab ? 'text-white' : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'}`}
                        >
                            {activeTab === tab && (
                                <motion.div
                                    layoutId="activeUserStatusTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-20">
                                {tab === 'active' ? t('userpage.active', 'Active') : t('userpage.inactive', 'Inactive')}
                            </span>
                        </button>
                    ))}
                </div>

                <div className="relative w-full sm:w-72">
                    <input
                        data-testid="users-search-input"
                        type="text"
                        placeholder={t('userpage.search_placeholder', 'Search name / email / code…')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value.replace(/[<>]/g, '').slice(0, 50))}
                        maxLength={50}
                        className="w-full p-2 pl-10 pr-8 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                    />
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                </div>

                <select
                    data-testid="users-role-filter"
                    value={roleFilter}
                    onChange={(e) => { setRoleFilter(e.target.value); setCurrentPage(1); }}
                    className="tt-select w-full sm:w-52 p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                >
                    <option value="">{t('userpage.all_roles', 'All roles')}</option>
                    {(roles || []).map((r) => (
                        <option key={r.userRoleId} value={r.userRoleId}>{r.name}</option>
                    ))}
                </select>
            </div>

            {staffListLoading ? (
                <div className="flex flex-col items-center justify-center py-4 pt-12">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('userpage.loading', 'Loading...')}</span>
                </div>
            ) : (
                <>
                    <div className="hidden sm:block overflow-x-auto rounded-md border border-gray-200">
                        <table data-testid="users-table" className="w-full border-collapse rounded-md overflow-hidden">
                            <thead>
                                <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('userpage.serial_no', 'S.No')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('userpage.name', 'Name')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('userpage.role', 'Role')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('userpage.departments', 'Departments')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('userpage.contact', 'Contact')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('userpage.status', 'Status')}</th>
                                    <th className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('userpage.action', 'Action')}</th>
                                </tr>
                            </thead>
                            <tbody data-testid="users-table-body">
                                {(staffList || []).length > 0 ? staffList.map((s, index) => {
                                    const isActive = s.status === 'active';
                                    return (
                                        <tr key={s.staffId} data-testid={`user-row-${s.staffId}`} className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300 group">
                                            <td className="p-3 text-xs text-gray-600">{indexOfFirst + index + 1}</td>
                                            <td className="p-3 text-xs text-gray-600">
                                                <div className="flex items-center gap-3">
                                                    <div className="relative w-10 h-10 flex-shrink-0">
                                                        {s.image ? (
                                                            <img src={getFileURL(s.image)} alt={s.name || 'User'}
                                                                className="w-10 h-10 rounded-full object-cover shadow-md"
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    const fb = e.target.parentElement.querySelector('.image-fallback');
                                                                    if (fb) fb.style.display = 'flex';
                                                                }} />
                                                        ) : null}
                                                        <div className={`image-fallback ${s.image ? 'hidden' : 'flex'} absolute inset-0 w-10 h-10 rounded-full bg-[#4ABDBD] items-center justify-center shadow-md`}>
                                                            {initialsOf(s)
                                                                ? <span className="text-white text-sm font-bold uppercase">{initialsOf(s)}</span>
                                                                : <User className="w-5 h-5 text-white" />}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="font-semibold text-gray-700">{`${s.name || ''} ${s.surname || ''}`.trim() || '-'}</div>
                                                        {s.designation && <div className="text-[11px] text-slate-400">{s.designation}</div>}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-3 text-xs">
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#4ABDBD]/10 text-[#2D8A8A] text-[11px] font-semibold">
                                                    {s.roleName || '-'}
                                                </span>
                                                {/* A person attached to a switched-off role can sign in to nothing.
                                                    Saying so here is the only place an admin would find out. */}
                                                {s.roleStatus === 'inactive' && (
                                                    <div className="text-[10px] text-amber-600 mt-1">{t('userpage.role_inactive', 'role is inactive')}</div>
                                                )}
                                            </td>
                                            <td className="p-3 text-xs text-gray-600">
                                                {(s.departments || []).length
                                                    ? <div className="flex flex-wrap gap-1">
                                                        {s.departments.map((d) => (
                                                            <span key={d} className="px-2 py-0.5 rounded bg-gray-100 text-[11px] text-gray-600">{d}</span>
                                                        ))}
                                                    </div>
                                                    : '-'}
                                            </td>
                                            <td className="p-3 text-xs text-gray-600">
                                                <div>{s.email || '-'}</div>
                                                <div className="text-[11px] text-slate-400">{s.phone || ''}</div>
                                            </td>
                                            <td className="p-3 text-xs">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        type="button" role="switch" aria-checked={isActive}
                                                        data-testid={`user-status-toggle-${s.staffId}`}
                                                        onClick={() => !mutating && handleStatusToggle(s)}
                                                        disabled={mutating}
                                                        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-300 ${isActive ? 'bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A]' : 'bg-gray-300'} ${mutating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                    >
                                                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-300 ${isActive ? 'translate-x-6' : 'translate-x-1'}`} />
                                                    </button>
                                                    <span className={`text-xs font-semibold ${isActive ? 'text-green-700' : 'text-gray-500'}`}>
                                                        {isActive ? t('userpage.active', 'Active') : t('userpage.inactive', 'Inactive')}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="p-3 text-xs">
                                                <div className="flex justify-center gap-2">
                                                    <button data-testid={`edit-user-${s.staffId}`} onClick={() => openEdit(s)}
                                                        title={t('userpage.edit', 'Edit')}
                                                        className="text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md cursor-pointer transition-all duration-300 hover:scale-110">
                                                        <Pencil className="h-4 w-4" />
                                                    </button>
                                                    <button data-testid={`reset-password-${s.staffId}`} onClick={() => handleResetPassword(s)}
                                                        title={t('userpage.reset_password', 'Reset password')}
                                                        className="text-amber-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 hover:from-amber-500 hover:to-orange-500 shadow-md cursor-pointer transition-all duration-300 hover:scale-110">
                                                        <KeyRound className="h-4 w-4" />
                                                    </button>
                                                    <button data-testid={`delete-user-${s.staffId}`} onClick={() => handleDelete(s)}
                                                        disabled={mutating}
                                                        title={t('userpage.delete', 'Delete')}
                                                        className={`text-red-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-500 hover:to-pink-500 shadow-md cursor-pointer transition-all duration-300 hover:scale-110 ${mutating ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }) : (
                                    <tr>
                                        <td colSpan={7} className="p-6 text-center text-gray-500">
                                            {t('userpage.no_users', 'No users yet. Add the people who work here to give them a login.')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Cards on mobile */}
                    <div className="block sm:hidden space-y-4">
                        {(staffList || []).length > 0 ? staffList.map((s) => {
                            const isActive = s.status === 'active';
                            return (
                                <div key={s.staffId} data-testid={`user-card-${s.staffId}`} className="p-4 bg-gray-50 rounded-xl shadow-md">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <div className="font-semibold text-gray-800 text-sm">{`${s.name || ''} ${s.surname || ''}`.trim() || '-'}</div>
                                            <span className="inline-flex mt-1 px-2 py-0.5 rounded-full bg-[#4ABDBD]/10 text-[#2D8A8A] text-[11px] font-semibold">
                                                {s.roleName || '-'}
                                            </span>
                                        </div>
                                        <div className="flex gap-2">
                                            <button onClick={() => openEdit(s)} className="text-[#4ABDBD] p-2 rounded-xl bg-blue-50"><Pencil className="h-4 w-4" /></button>
                                            <button onClick={() => handleResetPassword(s)} className="text-amber-500 p-2 rounded-xl bg-amber-50"><KeyRound className="h-4 w-4" /></button>
                                            <button onClick={() => handleDelete(s)} className="text-red-500 p-2 rounded-xl bg-red-50"><Trash2 className="h-4 w-4" /></button>
                                        </div>
                                    </div>
                                    <div className="text-xs text-gray-600">{s.email || '-'}</div>
                                    <div className="text-xs text-gray-600">{s.phone || ''}</div>
                                    {(s.departments || []).length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {s.departments.map((d) => (
                                                <span key={d} className="px-2 py-0.5 rounded bg-white text-[11px] text-gray-600 border border-gray-200">{d}</span>
                                            ))}
                                        </div>
                                    )}
                                    <div className="text-xs flex items-center gap-2 mt-3">
                                        <button type="button" role="switch" aria-checked={isActive}
                                            onClick={() => !mutating && handleStatusToggle(s)} disabled={mutating}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full ${isActive ? 'bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A]' : 'bg-gray-300'}`}>
                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${isActive ? 'translate-x-6' : 'translate-x-1'}`} />
                                        </button>
                                        <span className={`font-semibold ${isActive ? 'text-green-700' : 'text-gray-500'}`}>
                                            {isActive ? t('userpage.active', 'Active') : t('userpage.inactive', 'Inactive')}
                                        </span>
                                    </div>
                                </div>
                            );
                        }) : (
                            <div className="p-6 text-center text-gray-500 text-sm">
                                {t('userpage.no_users', 'No users yet. Add the people who work here to give them a login.')}
                            </div>
                        )}
                    </div>
                </>
            )}

            {!staffListLoading && staffTotal > 0 && (
                <Pagination
                    totalItems={staffTotal}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                />
            )}

            <AddUser
                isOpen={isModalOpen}
                editing={editing}
                roles={roles}
                onClose={() => { setIsModalOpen(false); setEditing(null); }}
                onSaved={refetch}
            />
        </div>
    );
}
